import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { configurationsApi } from '@/lib/api/configurations.api';

import type {
  ConfigurationSetup,
  CreateConfigurationSetupDto,
  UpdateConfigurationSetupDto,
  CreateConfigurationVersionDto,
} from '@repo/shared-types';

export function useConfigurations(enabled = true) {
  return useQuery({
    queryKey: ['configurations'],
    queryFn: () => configurationsApi.getConfigurations(),
    enabled,
  });
}

export function useConfiguration(id: string) {
  return useQuery({
    queryKey: ['configurations', id],
    queryFn: () => configurationsApi.getConfiguration(id),
    enabled: !!id,
  });
}

export function useCreateConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateConfigurationSetupDto) => configurationsApi.createConfiguration(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
    },
  });
}

export function useUpdateConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateConfigurationSetupDto }) =>
      configurationsApi.updateConfiguration(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
      queryClient.invalidateQueries({ queryKey: ['configurations', id] });
    },
  });
}

export function useDeleteConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => configurationsApi.deleteConfiguration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
    },
  });
}

export function useTestConfiguration() {
  return useMutation({
    mutationFn: ({ id, phoneNumber }: { id: string; phoneNumber: string }) =>
      configurationsApi.testConfiguration(id, phoneNumber),
  });
}

export function useCreateVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ configId, data }: { configId: string; data: CreateConfigurationVersionDto }) =>
      configurationsApi.createVersion(configId, data),
    onSuccess: (newVersion, { configId }) => {
      queryClient.setQueryData(['configurations'], (old: ConfigurationSetup[] | undefined) => {
        if (!old) return old;
        return old.map((c) => {
          if (c.id === configId) {
            return {
              ...c,
              versions: [newVersion, ...(c.versions || [])],
            };
          }
          return c;
        });
      });
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
      queryClient.invalidateQueries({ queryKey: ['configurations', configId] });
    },
  });
}

export function useSetActiveVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ configId, versionId }: { configId: string; versionId: string }) =>
      configurationsApi.setActiveVersion(configId, versionId),
    onSuccess: (updatedConfig, { configId }) => {
      queryClient.setQueryData(['configurations'], (old: ConfigurationSetup[] | undefined) => {
        if (!old) return old;
        return old.map((c) => (c.id === configId ? updatedConfig : c));
      });
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
      queryClient.invalidateQueries({ queryKey: ['configurations', configId] });
    },
  });
}

export function useDeleteVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ configId, versionId }: { configId: string; versionId: string }) =>
      configurationsApi.deleteVersion(configId, versionId),
    onSuccess: (_, { configId }) => {
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
      queryClient.invalidateQueries({ queryKey: ['configurations', configId] });
    },
  });
}
